// eslint-disable-next-line no-unused-vars
import React, { forwardRef } from 'react';
import PropTypes from 'prop-types';
import styled, { css } from 'styled-components';

import { Input } from '../Input';

const TextInputOptions = {
    size: ['xs', 'sm', 'md', 'lg'],
    sizeRatio: {
        xs: 0.75,
        sm: 0.83,
        md: 1,
        lg: 1.17
    },
    type: ['text', 'tel', 'email', 'password', 'url', 'number']
};

export const TextInputStyles = css`
    flex-grow: 1;
    flex-basis: 100%;
    background: transparent;
    border: ${({ withborder }) => (withborder ? '1px solid hsl(0,0%,80%)' : 'none')};
    border-radius: 3px;
    box-sizing: border-box;
    color: ${(props) => props.theme.colors.text.default};
    font-family: ${(props) => props.theme.typography.fonts.ui};
    font-size: ${(props) => TextInputOptions.sizeRatio[props.size] * 1.066}rem;
    font-weight: 400;
    line-height: ${(props) =>
        props.multiline
            ? `${(props) => TextInputOptions.sizeRatio[props.size] * 1.6}rem`
            : `${(props) => TextInputOptions.sizeRatio[props.size] * 1.1}rem`};
    padding: ${({ size }) => (size === 'xs' ? '0.455rem 8px' : '0.667rem 0.84rem')};
    text-align: ${(props) => props.valueAlign || `left`};
    :focus {
        outline: none;
    }
    :hover {
        cursor: ${(props) => (props.disabled && !props.readOnly ? `not-allowed` : `text`)};
    }
    ::placeholder,
    :-ms-input-placeholder,
    ::-ms-input-placeholder {
        color: ${(props) => props.theme.colors.monochrome.MC2};
        opacity: 1;
    }
    ${({ noresize }) =>
        noresize &&
        css`
            resize: none;
        `}
`;

const TextInput = styled(({ multiline, readOnly, size, valueAlign, righticon, ...rest }) => <input {...rest} />)`
    ${TextInputStyles}
    ${({ righticon }) =>
        righticon &&
        css`
            padding-right: 3rem;
        `}
    ${({ disablepointerevents }) =>
        disablepointerevents &&
        css`
            pointer-events: none;
        `}
`;

const TextInputRightIcon = styled.div`
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    padding: 6px;
    background: #fff;
    width: 3rem;
    display: flex;
    justify-content: center;
    align-items: center;
    pointer-events: none;
`;

export const TextField = forwardRef(
    (
        {
            disabled,
            error,
            inputProps,
            multiline,
            prefix,
            readOnly,
            suffix,
            innerPrefix,
            iconRight,
            withBorder,
            height,
            ...props
        },
        ref
    ) => {
        const a11yProps = {};
        const inputPropsRemapped = {
            disabled: disabled,
            error: error,
            readOnly: readOnly,
            prefix,
            suffix,
            ...inputProps
        };

        return (
            <Input {...inputPropsRemapped}>
                {innerPrefix}
                <TextInput
                    ref={ref}
                    as={multiline ? 'textarea' : 'input'}
                    disabled={disabled || readOnly}
                    readOnly={readOnly}
                    righticon={iconRight}
                    disablepointerevents={typeof inputProps?.onClick === 'function'}
                    withborder={withBorder}
                    {...a11yProps}
                    {...props}
                    {...(multiline && readOnly ? { noresize: true } : {})}
                    {...(height ? { style: { height } } : {})}
                />
                {iconRight ? <TextInputRightIcon>{iconRight}</TextInputRightIcon> : null}
            </Input>
        );
    }
);

TextField.displayName = 'TextField';
TextField.propTypes = {
    /**
     * Enables browser autocomplete, or if you are feeling nice to your users,
     * allows for a [variety of string options](https://developer.mozilla.org/en-US/docs/Web/HTML/Attributes/autocomplete#Values)
     * to provide context to the browser, password managers, etc.
     */
    autoComplete: PropTypes.string,
    disabled: PropTypes.bool,
    /**
     * **Please note,** that this is typically set at the `<FormField />`
     * level, and not on this lower level element component.
     *
     * There are three ways to indicate a field error: by bassing a boolean
     * value to render the label with red text, and add a red outline to
     * the input field (if applicable); by passing a string to be displayed
     * underneath the field; or by passing an object with a `body:` parameter
     * for the message to be displayed.
     *
     * We recommend for greatest forward flexibility, using the object option
     * where possible, as future extensibility of error status rendering will
     * be controlled through additional object parameters.
     *
     * ```javascript
     * {
     *   body: ``,
     * }
     * ```
     */
    error: PropTypes.oneOfType([PropTypes.bool, PropTypes.object, PropTypes.string]),
    height: PropTypes.string,
    iconRight: PropTypes.node,
    id: PropTypes.string.isRequired,
    /**
     * If desired, an object of parameters to be spread over the `<Input />`
     * element.
     */
    inputProps: PropTypes.object,
    /**
     * Enables display and functionality of `<textarea />` input.
     */
    multiline: PropTypes.bool,
    name: PropTypes.string.isRequired,
    onChange: PropTypes.func.isRequired,
    placeholder: PropTypes.string,
    innerPrefix: PropTypes.node,
    prefix: PropTypes.node,
    readOnly: PropTypes.bool,
    size: PropTypes.oneOf([...TextInputOptions.size]),
    suffix: PropTypes.node,
    /**
     * Sets the supported input type; useful for responsive interfaces.
     */
    type: PropTypes.oneOf([...TextInputOptions.type]).isRequired,
    value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    /**
     * Sets the text-align of the value within the input wrapper.
     */
    valueAlign: PropTypes.string,
    withBorder: PropTypes.bool
};

TextField.defaultProps = {
    autoComplete: 'on',
    disabled: false,
    error: false,
    inputProps: {},
    innerPrefix: null,
    multiline: false,
    onChange: () => null,
    readOnly: false,
    size: 'md',
    type: 'text',
    valueAlign: 'left'
};
